import { useState } from "react";
import { Mail, Phone, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Images from "../../../utils/Images";
import { Link, useNavigate } from "react-router-dom";

export default function ForgotPassword() {
  const [method, setMethod] = useState("email"); // "email" or "phone"
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const navigate = useNavigate();

  const handleContinue = () => {
    const contactValue = method === "email" ? email : phone;
    if (contactValue.trim()) {
      // Navigate to OTP verification with the contact method and value
      navigate("/forgot-password/verify", { 
        state: { 
          method, 
          contact: contactValue 
        } 
      });
    }
  };

  return (
    <div className="relative min-h-screen">
      <img 
        src="https://images.unsplash.com/photo-1551632811-561732d1e306?q=80&w=1600&auto=format&fit=crop" 
        alt="bg" 
        className="object-cover absolute inset-0 w-full h-full" 
      />
      <div className="absolute inset-0 bg-black/60" />
      
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="flex relative z-10 justify-center items-start pt-10"
      >
        <img src={Images.logo_navbar} alt="TripSecure" className="h-10" />
      </motion.div>
      
      <div className="flex relative z-10 justify-center items-center p-6">
        <motion.div 
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          className="p-6 mt-4 w-full max-w-md rounded-3xl shadow-2xl bg-white/95"
        >
          <h2 className="text-lg font-semibold text-center">Reset Password</h2>
          <p className="mt-1 text-xs text-center text-gray-500">
            Enter your email or phone number to receive a reset code
          </p>

          {/* Method Selection */}
          <div className="flex relative p-1 mt-5 bg-gray-50 rounded-lg border border-gray-200">
            <motion.div
              className="absolute inset-y-1 bg-white rounded-md shadow-sm"
              initial={false}
              animate={{
                x: method === "email" ? 0 : "100%",
                width: "calc(50% - 4px)"
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
            <button
              onClick={() => setMethod("email")}
              className={`flex-1 rounded-md py-2 text-xs font-medium transition-all duration-200 relative z-10 ${
                method === "email"
                  ? "text-emerald-600"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Email
            </button>
            <button
              onClick={() => setMethod("phone")}
              className={`flex-1 rounded-md py-2 text-xs font-medium transition-all duration-200 relative z-10 ${
                method === "phone"
                  ? "text-emerald-600"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Phone
            </button>
          </div>

          <div className="mt-4 space-y-4">
            {/* Input Field */}
            <AnimatePresence mode="wait">
              <motion.div 
                key={method}
                initial={{ opacity: 0, x: method === "email" ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: method === "email" ? 20 : -20 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="relative group"
              >
                <div className="relative rounded-xl p-[1px] bg-gradient-to-br from-emerald-400/30 via-transparent to-emerald-400/30 transition-all duration-300 group-hover:from-emerald-400/50 group-hover:to-emerald-400/50 group-focus-within:from-emerald-500/60 group-focus-within:to-emerald-500/60">
                  <div className="flex items-center gap-3 rounded-[0.95rem] bg-white/95 px-4 py-3 ring-1 ring-gray-200/50 shadow-sm backdrop-blur-md transition-all duration-300 group-hover:ring-emerald-300/60 group-hover:shadow-md group-focus-within:ring-emerald-400/80 group-focus-within:shadow-lg group-focus-within:bg-white">
                    <motion.div
                      initial={{ rotate: 0, scale: 0.8 }}
                      animate={{ rotate: 0, scale: 1 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                    >
                      {method === "email" ? (
                        <Mail size={18} className="text-emerald-600 transition-colors duration-300 group-focus-within:text-emerald-700" />
                      ) : (
                        <Phone size={18} className="text-emerald-600 transition-colors duration-300 group-focus-within:text-emerald-700" />
                      )}
                    </motion.div>
                    <input
                      type={method === "email" ? "email" : "tel"}
                      value={method === "email" ? email : phone}
                      onChange={(e) => method === "email" ? setEmail(e.target.value) : setPhone(e.target.value)}
                      placeholder={method === "email" ? "Enter your email" : "Enter your phone number"}
                      className="w-full text-sm font-medium bg-transparent border-0 transition-colors duration-300 outline-none focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none placeholder:text-gray-400 placeholder:font-normal focus:text-gray-900"
                    />
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Continue Button */}
            <motion.button
              onClick={handleContinue}
              disabled={!(method === "email" ? email.trim() : phone.trim())}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
              className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg transition-all duration-300 group hover:from-emerald-600 hover:to-emerald-700 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              <span className="flex relative z-10 gap-2 justify-center items-center">
                Send Reset Code 
                <motion.div
                  animate={{ x: [0, 3, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <ArrowRight size={16} />
                </motion.div>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r transition-transform duration-700 -translate-x-full from-white/0 via-white/20 to-white/0 group-hover:translate-x-full"></div>
            </motion.button>

            {/* Back to Sign In */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.3 }}
            >
              <Link 
                to="/signin" 
                className="block text-xs text-center text-emerald-600 transition-colors hover:text-emerald-700"
              >
                Back to Sign In
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
